import type { TemplatePrompts } from '@kimuno/api-sdk/lib/types/common/Conversation';

import { createClassYearOptions, FieldName } from './TemplatePromptForm/formConfigs';
import { schoolRestrictedTemplatePromptIds, startPageTemplatePromptIds } from './utils';

// Allowlisted query keys a dashboard card may set on `/ki-tools`. Nothing else is
// ever read from the URL — no prompt body, attachment, or free-text message key.
export const LAUNCH_PARAM_KEYS = {
	template: 'template',
	subject: 'subject',
	classYear: 'classYear',
} as const;

// The subject only ever seeds the SUBJECT form field, which the user already edits
// freely, so injection is impossible — but cap length and strip to a single line to
// keep a hand-crafted URL from stuffing the field with unbounded/control content.
const SUBJECT_MAX_LENGTH = 100;

export interface TemplateLaunchIntent {
	template: TemplatePrompts;
	prefill: Partial<Record<FieldName, string>>;
}

type LaunchQuery = Record<string, string | string[] | undefined | null>;

const firstValue = (value: LaunchQuery[string]): string | undefined =>
	Array.isArray(value) ? value[0] : (value ?? undefined);

const isStartPageTemplate = (value: string): value is TemplatePrompts =>
	(startPageTemplatePromptIds as string[]).includes(value);

// Collapse control characters (incl. newlines/tabs) and DEL to spaces so a
// hand-crafted subject stays a single plain-text line.
const stripControlChars = (raw: string): string =>
	Array.from(raw)
		.map((ch) => {
			const code = ch.codePointAt(0) ?? 0;
			return code < 0x20 || code === 0x7f ? ' ' : ch;
		})
		.join('');

const sanitizeSubject = (raw: string): string | undefined => {
	const plain = stripControlChars(raw).replace(/\s+/g, ' ').trim();
	if (!plain) return undefined;
	return plain.slice(0, SUBJECT_MAX_LENGTH);
};

const validClassYears = new Set(createClassYearOptions().map(({ value }) => value));

const validateClassYear = (raw: string): string | undefined =>
	validClassYears.has(raw) ? raw : undefined;

/**
 * Parse and fully validate a dashboard launch intent from the `/ki-tools` query.
 * Returns `null` when the `template` key is missing or not a start-page template.
 * Invalid `subject` / `classYear` values are dropped individually rather than
 * failing the whole intent, so a launch degrades to a bare template pre-select.
 */
export const parseLaunchParams = (
	query: LaunchQuery | undefined | null
): TemplateLaunchIntent | null => {
	if (!query) return null;

	const rawTemplate = firstValue(query[LAUNCH_PARAM_KEYS.template]);
	if (!rawTemplate || !isStartPageTemplate(rawTemplate)) return null;

	const prefill: Partial<Record<FieldName, string>> = {};

	const rawSubject = firstValue(query[LAUNCH_PARAM_KEYS.subject]);
	if (rawSubject) {
		const subject = sanitizeSubject(rawSubject);
		if (subject) prefill[FieldName.SUBJECT] = subject;
	}

	const rawClassYear = firstValue(query[LAUNCH_PARAM_KEYS.classYear]);
	if (rawClassYear) {
		const classYear = validateClassYear(rawClassYear);
		if (classYear) prefill[FieldName.CLASS_YEAR] = classYear;
	}

	return { template: rawTemplate, prefill };
};

/**
 * A launch never bypasses the existing entitlement gate: the image and podcast
 * templates stay hidden for school users, so a hand-crafted URL targeting one
 * must fall back to the normal landing.
 */
export const isLaunchTemplateEntitled = (
	template: TemplatePrompts,
	isSchoolUser: boolean
): boolean => !(isSchoolUser && (schoolRestrictedTemplatePromptIds as string[]).includes(template));
